import DepositInvoicePayment from '../components/DepositInvoicePayment';
import React, { useState, useEffect } from 'react';
import { damageApi, vehicleApi, customerApi, bookingApi, formatApiError } from '../services/api';
import {
  Warning, Plus, PencilSimple, Trash, Camera, Receipt as ReceiptIcon,
  Car, User, Circle, CheckCircle, X as CloseIcon, Wrench
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import VehicleSketch from '../components/VehicleSketch';
import DamageCatalogPicker from '../components/DamageCatalogPicker';
import { isTotalLoss, totalLossSB, applyDamageCatalog } from '../lib/damageBilling';
import { useNavigate, useSearchParams } from 'react-router-dom';

const DAMAGE_TYPES = ['Kratzer', 'Delle', 'Beule', 'Steinschlag', 'Glasbruch', 'Reifenschaden', 'Lackschaden', 'Innenraumschaden', 'Totalschaden', 'Sonstiges'];
const SEVERITIES = [
  { value: 'minor', label: 'Gering', color: 'bg-green-100 text-green-700' },
  { value: 'medium', label: 'Mittel', color: 'bg-orange-100 text-orange-700' },
  { value: 'major', label: 'Schwer', color: 'bg-red-100 text-red-700' }
];
const STATUSES = [
  { value: 'open', label: 'Offen', color: 'bg-red-100 text-red-700' },
  { value: 'in_progress', label: 'In Bearbeitung', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'resolved', label: 'Erledigt', color: 'bg-blue-100 text-blue-700' },
  { value: 'invoiced', label: 'Abgerechnet', color: 'bg-green-100 text-green-700' }
];

const emptyForm = {
  vehicle_id: '',
  booking_id: '',
  customer_id: '',
  damage_type: 'Kratzer',
  position: '',
  description: '',
  severity: 'minor',
  status: 'open',
  estimated_cost: '',
  actual_cost: '',
  deductible_applied: '',
  repair_notes: '',
  markers: []
};

const Damages = () => {
  const [damages, setDamages] = useState([]);
  const [stats, setStats] = useState(null);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [photoFiles, setPhotoFiles] = useState([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => { loadAll(); }, [filterStatus]);

  // Auto-open form when ?booking=XXX is in URL (from BookingDetail)
  useEffect(() => {
    const bookingId = searchParams.get('booking');
    if (bookingId && bookings.length > 0 && !showModal) {
      const b = bookings.find(x => x.id === bookingId);
      if (b) {
        setEditing(null);
        setFormData({
          ...emptyForm,
          booking_id: bookingId,
          vehicle_id: b.vehicle_id,
          customer_id: b.customer_id,
          deductible_applied: b.insurance_deductible ?? '',
        });
        setPhotoFiles([]);
        setShowModal(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, searchParams]);

  const loadAll = async () => {
    try {
      const params = filterStatus !== 'all' ? { status: filterStatus } : {};
      const [dRes, sRes, vRes, cRes, bRes] = await Promise.all([
        damageApi.getAll(params),
        damageApi.getStats(),
        vehicleApi.getAll(),
        customerApi.getAll(),
        bookingApi.getAll()
      ]);
      setDamages(dRes.data);
      setStats(sRes.data);
      setVehicles(vRes.data);
      setCustomers(cRes.data);
      setBookings(bRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setFormData(emptyForm);
    setPhotoFiles([]);
    setShowModal(true);
  };

  const openEdit = (damage) => {
    setEditing(damage);
    setFormData({
      vehicle_id: damage.vehicle_id,
      booking_id: damage.booking_id || '',
      customer_id: damage.customer_id || '',
      damage_type: damage.damage_type || 'Kratzer',
      position: damage.position || '',
      description: damage.description || '',
      severity: damage.severity || 'minor',
      status: damage.status || 'open',
      estimated_cost: damage.estimated_cost ?? '',
      actual_cost: damage.actual_cost ?? '',
      deductible_applied: damage.deductible_applied ?? '',
      repair_notes: damage.repair_notes || '',
      markers: damage.markers || []
    });
    setPhotoFiles([]);
    setShowModal(true);
  };

  const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : parseFloat(v));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        booking_id: formData.booking_id || null,
        customer_id: formData.customer_id || null,
        estimated_cost: numOrNull(formData.estimated_cost),
        actual_cost: numOrNull(formData.actual_cost),
        deductible_applied: numOrNull(formData.deductible_applied)
      };
      let damageId;
      if (editing) {
        delete payload.vehicle_id;
        await damageApi.update(editing.id, payload);
        damageId = editing.id;
      } else {
        if (!payload.vehicle_id) {
          toast.error('Bitte Fahrzeug auswählen');
          return;
        }
        const res = await damageApi.create(payload);
        damageId = res.data.id;
      }
      // Upload photos
      for (const f of photoFiles) {
        try { await damageApi.uploadPhoto(damageId, f); } catch (_) {}
      }
      toast.success(editing ? 'Schaden aktualisiert' : 'Schaden erfasst');
      setShowModal(false);
      setEditing(null);
      setFormData(emptyForm);
      setPhotoFiles([]);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Schaden wirklich löschen?')) return;
    try {
      await damageApi.delete(id);
      toast.success('Schaden gelöscht');
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleCreateInvoice = async (id) => {
    try {
      const res = await damageApi.createInvoice(id);
      toast.success(`Rechnung ${res.data.invoice_number} erstellt`);
      loadAll();
      navigate(`/invoices`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleBookingChange = (bookingId) => {
    const b = bookings.find(x => x.id === bookingId);
    if (b) {
      setFormData(prev => ({
        ...prev,
        booking_id: bookingId,
        vehicle_id: b.vehicle_id,
        customer_id: b.customer_id,
        deductible_applied: isTotalLoss(prev) ? totalLossSB(b, vehicles.find(v => v.id === b.vehicle_id)) : (prev.deductible_applied || (b.insurance_deductible ?? ''))
      }));
    } else {
      setFormData(prev => ({ ...prev, booking_id: '', deductible_applied: isTotalLoss(prev) ? totalLossSB(null, vehicles.find(v => v.id === prev.vehicle_id)) : prev.deductible_applied }));
    }
  };

  const addMarker = (view, x, y) => {
    setFormData(prev => ({ ...prev, markers: [...prev.markers, { view, x, y }] }));
  };
  const removeMarker = (idx) => {
    setFormData(prev => ({ ...prev, markers: prev.markers.filter((_, i) => i !== idx) }));
  };

  const statusBadge = (status) => {
    const s = STATUSES.find(x => x.value === status) || STATUSES[0];
    return <span className={`px-2 py-0.5 rounded-sm text-xs font-semibold ${s.color}`}>{s.label}</span>;
  };
  const severityBadge = (sev) => {
    const s = SEVERITIES.find(x => x.value === sev) || SEVERITIES[0];
    return <span className={`px-2 py-0.5 rounded-sm text-xs font-semibold ${s.color}`}>{s.label}</span>;
  };

  const fmt = (v) => v == null ? '—' : `€ ${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div data-testid="damages-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <p className="overline text-gray-500 mb-2">Fahrzeuge</p>
          <h1 className="h1 text-[#1a1a1a]">Schadensmanagement</h1>
          <p className="body text-gray-600 mt-1">Alle erfassten Schäden zentral verwalten, abrechnen und dokumentieren.</p>
        </div>
        <Button className="btn-primary" onClick={openNew} data-testid="add-damage-btn">
          <Plus size={18} className="mr-2" />
          Schaden erfassen
        </Button>
      </div>

      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="card-swiss p-4 flex items-center gap-3">
            <Warning size={28} weight="duotone" className="text-[#E60000]" />
            <div>
              <p className="text-xs text-gray-500">Gesamt</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
          </div>
          <div className="card-swiss p-4 flex items-center gap-3">
            <Circle size={28} weight="fill" className="text-red-500" />
            <div>
              <p className="text-xs text-gray-500">Offen</p>
              <p className="text-2xl font-bold">{stats.open}</p>
            </div>
          </div>
          <div className="card-swiss p-4 flex items-center gap-3">
            <Wrench size={28} weight="duotone" className="text-yellow-600" />
            <div>
              <p className="text-xs text-gray-500">In Bearbeitung</p>
              <p className="text-2xl font-bold">{stats.by_status?.in_progress?.count || 0}</p>
            </div>
          </div>
          <div className="card-swiss p-4 flex items-center gap-3">
            <CheckCircle size={28} weight="duotone" className="text-green-600" />
            <div>
              <p className="text-xs text-gray-500">Abgerechnet</p>
              <p className="text-2xl font-bold">{stats.by_status?.invoiced?.count || 0}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filter row */}
      <div className="flex flex-wrap gap-2 mb-4">
        {['all', ...STATUSES.map(s => s.value)].map(v => (
          <button
            key={v}
            onClick={() => setFilterStatus(v)}
            className={`px-3 py-1.5 rounded-sm text-sm font-semibold border transition-colors ${
              filterStatus === v
                ? 'bg-[#E60000] text-white border-[#E60000]'
                : 'bg-white text-gray-700 border-[#E2E4E9] hover:bg-gray-50'
            }`}
            data-testid={`filter-${v}`}
          >
            {v === 'all' ? 'Alle' : STATUSES.find(s => s.value === v)?.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Lade Schäden…</div>
      ) : damages.length === 0 ? (
        <div className="card-swiss p-12 text-center">
          <Warning size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="h3 mb-2">Keine Schäden erfasst</h3>
          <p className="body text-gray-600 mb-6">Schäden werden hier zentral dokumentiert und nachverfolgt.</p>
          <Button className="btn-primary" onClick={openNew}>
            <Plus size={18} className="mr-2" />
            Ersten Schaden erfassen
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full card-swiss">
            <thead>
              <tr className="border-b border-[#E2E4E9] bg-[#F7F8FA]">
                <th className="text-left p-3 overline text-gray-600">Datum</th>
                <th className="text-left p-3 overline text-gray-600">Fahrzeug</th>
                <th className="text-left p-3 overline text-gray-600">Kunde</th>
                <th className="text-left p-3 overline text-gray-600">Art</th>
                <th className="text-left p-3 overline text-gray-600">Position</th>
                <th className="text-left p-3 overline text-gray-600">Schwere</th>
                <th className="text-left p-3 overline text-gray-600">Status</th>
                <th className="text-left p-3 overline text-gray-600">Zahlung</th>
                <th className="text-right p-3 overline text-gray-600">Kosten</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {damages.map(d => (
                <tr key={d.id} className="border-b border-[#E2E4E9] hover:bg-[#F7F8FA]" data-testid={`damage-row-${d.id}`}>
                  <td className="p-3 text-sm">
                    {d.reported_date ? new Date(d.reported_date).toLocaleDateString('de-DE') : '—'}
                  </td>
                  <td className="p-3 text-sm">
                    <div className="flex items-center gap-2">
                      <Car size={16} className="text-gray-500" />
                      <div>
                        <div className="font-medium">{d.vehicle?.brand} {d.vehicle?.model}</div>
                        <div className="text-xs text-gray-500">{d.vehicle?.license_plate}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-sm">
                    {d.customer ? (
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-gray-500" />
                        <span>{d.customer.first_name} {d.customer.last_name}</span>
                      </div>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="p-3 text-sm">{d.damage_type || '—'}</td>
                  <td className="p-3 text-sm">{d.position || '—'}</td>
                  <td className="p-3">{severityBadge(d.severity)}</td>
                  <td className="p-3">{statusBadge(d.status)}</td>
                  <td className="p-3 text-sm" data-testid={`damage-payment-${d.id}`}>
                    {d.invoice ? <><span className={`text-xs font-semibold ${d.invoice.status==='cancelled'?'text-slate-500':d.invoice.open_amount<=0?'text-emerald-700':d.invoice.paid_amount>0?'text-amber-700':'text-red-700'}`}>{d.invoice.status==='cancelled'?'Storniert':d.invoice.open_amount<=0?'Bezahlt':d.invoice.paid_amount>0?'Teilbezahlt':'Offen'}</span><div className="text-xs text-slate-500">{d.invoice.invoice_number}</div>{d.invoice.deposit_paid_amount>0&&<div className="text-xs text-emerald-700">{fmt(d.invoice.deposit_paid_amount)} aus Kaution verrechnet</div>}{d.invoice.open_amount>0&&<div className="text-xs text-slate-500">Rest: {fmt(d.invoice.open_amount)}</div>}</> : <span className="text-xs text-slate-500">Noch keine Rechnung</span>}
                  </td>
                  <td className="p-3 text-right text-sm">
                    <div>{fmt(isTotalLoss(d) ? d.deductible_applied : (d.actual_cost || d.estimated_cost))}</div>
                    {d.deductible_applied > 0 && (
                      <div className="text-xs text-gray-500">SB: {fmt(d.deductible_applied)}</div>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-end">
                      {d.invoice && d.invoice.open_amount>0 && d.invoice.status!=='cancelled' && <button onClick={()=>setPaymentTarget(d.invoice)} className="p-1.5 rounded-sm bg-blue-50 text-blue-800 text-xs font-semibold" title="Schadensrechnung aus einbehaltener Kaution bezahlen" data-testid={`pay-damage-deposit-${d.id}`}>Mit Kaution bezahlen</button>}
                      {d.status !== 'invoiced' && (isTotalLoss(d) ? d.deductible_applied > 0 : (d.actual_cost > 0 || d.deductible_applied > 0)) && d.customer_id && (
                        <button
                          onClick={() => handleCreateInvoice(d.id)}
                          className="p-1.5 rounded-sm hover:bg-green-50 text-green-700"
                          title="Rechnung erstellen"
                          data-testid={`invoice-damage-${d.id}`}
                        >
                          <ReceiptIcon size={16} />
                        </button>
                      )}
                      <button onClick={() => openEdit(d)} className="p-1.5 rounded-sm hover:bg-gray-100 text-gray-600" data-testid={`edit-damage-${d.id}`}>
                        <PencilSimple size={16} />
                      </button>
                      <button onClick={() => handleDelete(d.id)} className="p-1.5 rounded-sm hover:bg-red-50 text-red-600" data-testid={`delete-damage-${d.id}`}>
                        <Trash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DepositInvoicePayment invoice={paymentTarget} onClose={()=>setPaymentTarget(null)} onChanged={loadAll} />

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Schaden bearbeiten' : 'Neuen Schaden erfassen'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-center justify-between bg-[#FAFAFB] border border-[#E2E4E9] rounded-sm px-3 py-2">
              <div className="text-xs text-[#525866]">
                <span className="font-semibold text-[#111418]">Schnelle Auswahl</span> aus dem Schadenskatalog — Kosten und Beschreibung werden übernommen und sind weiter bearbeitbar.
              </div>
              <DamageCatalogPicker
                onSelect={(item) => {
                  setFormData(prev => applyDamageCatalog(prev, item,
                    bookings.find(b => b.id === prev.booking_id),
                    vehicles.find(v => v.id === prev.vehicle_id)));
                }}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline">Buchung (optional)</Label>
                <Select value={formData.booking_id || 'none'} onValueChange={(v) => handleBookingChange(v === 'none' ? '' : v)}>
                  <SelectTrigger className="input-swiss">
                    <SelectValue placeholder="Keine Buchung" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Keine Buchung</SelectItem>
                    {bookings.slice(0, 200).map(b => {
                      const v = vehicles.find(x => x.id === b.vehicle_id);
                      const c = customers.find(x => x.id === b.customer_id);
                      return (
                        <SelectItem key={b.id} value={b.id}>
                          #{b.id.substring(0, 8)} — {v?.license_plate || '?'} · {c ? `${c.first_name} ${c.last_name}` : '?'}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline">Fahrzeug *</Label>
                <Select value={formData.vehicle_id} onValueChange={(v) => setFormData(prev => isTotalLoss(prev) ? { ...prev, vehicle_id: v, booking_id: '', deductible_applied: totalLossSB(null, vehicles.find(vehicle => vehicle.id === v)) } : { ...prev, vehicle_id: v })} disabled={!!editing}>
                  <SelectTrigger className="input-swiss" data-testid="damage-vehicle-select">
                    <SelectValue placeholder="Fahrzeug wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicles.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} — {v.license_plate}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline">Kunde (optional)</Label>
                <Select value={formData.customer_id || 'none'} onValueChange={(v) => setFormData({ ...formData, customer_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="input-swiss">
                    <SelectValue placeholder="Kein Kunde" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Kein Kunde</SelectItem>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline">Schadensart</Label>
                <Select value={formData.damage_type} onValueChange={(v) => setFormData(prev => v === 'Totalschaden' ? { ...prev, damage_type: v, severity: 'major', deductible_applied: totalLossSB(bookings.find(b => b.id === prev.booking_id), vehicles.find(vehicle => vehicle.id === prev.vehicle_id), prev.deductible_applied) } : { ...prev, damage_type: v })}>
                  <SelectTrigger className="input-swiss">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAMAGE_TYPES.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline">Position (Freitext)</Label>
                <Input
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  className="input-swiss"
                  placeholder="z. B. Vorne links, Heckstoßstange Mitte"
                  data-testid="damage-position-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="overline">Schwere</Label>
                  <Select value={formData.severity} onValueChange={(v) => setFormData({ ...formData, severity: v })}>
                    <SelectTrigger className="input-swiss">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SEVERITIES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="overline">Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger className="input-swiss">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div>
              <Label className="overline">Beschreibung *</Label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-swiss w-full px-3 py-2"
                rows={3}
                required
                placeholder="Detaillierte Beschreibung des Schadens"
                data-testid="damage-description-input"
              />
            </div>

            <div className="pt-3 border-t border-[#E2E4E9]">
              <Label className="overline block mb-2">Schadens-Skizze</Label>
              <VehicleSketch
                vehicle={vehicles.find(v => v.id === formData.vehicle_id)}
                markers={formData.markers}
                onAddMarker={addMarker}
                onRemoveMarker={removeMarker}
              />
            </div>

            <div className="pt-3 border-t border-[#E2E4E9]">
              <Label className="overline block mb-2">Kosten (brutto)</Label>
              {isTotalLoss(formData) && <div className="mb-3 p-3 border border-blue-200 bg-blue-50 rounded-sm text-sm" data-testid="total-loss-sb-info">
                <p className="font-semibold">Totalschaden · Nur Selbstbeteiligung abrechnen</p>
                <p>Die SB wird aus der Buchung übernommen, ersatzweise aus dem Fahrzeug. Die Schadenshöhe dient der Dokumentation und wird nicht zusätzlich berechnet.</p>
                <p className="mt-1">Nachberechnung: {formData.deductible_applied === '' ? 'Bitte SB eintragen oder eine Buchung wählen.' : `${fmt(formData.deductible_applied)}${Number(formData.deductible_applied) === 0 ? ' · Keine Nachberechnung erforderlich.' : ''}`}</p>
                <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => setFormData(prev => ({ ...prev, deductible_applied: totalLossSB(bookings.find(b => b.id === prev.booking_id), vehicles.find(v => v.id === prev.vehicle_id)) }))}>SB aus Buchung/Fahrzeug übernehmen</Button>
              </div>}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Geschätzt (€)</Label>
                  <Input type="number" min={0} step={0.01} value={formData.estimated_cost} onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })} className="input-swiss" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">{isTotalLoss(formData) ? 'Schadenshöhe (nur Dokumentation, €)' : 'Reparaturkosten (€)'}</Label>
                  <Input type="number" min={0} step={0.01} value={formData.actual_cost} onChange={(e) => setFormData({ ...formData, actual_cost: e.target.value })} className="input-swiss" data-testid="damage-actual-cost" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Selbstbehalt (€)</Label>
                  <Input type="number" min={0} step={0.01} value={formData.deductible_applied} onChange={(e) => setFormData({ ...formData, deductible_applied: e.target.value })} className="input-swiss" data-testid="damage-deductible" />
                </div>
              </div>
            </div>

            <div>
              <Label className="overline">Reparaturnotizen</Label>
              <textarea
                value={formData.repair_notes}
                onChange={(e) => setFormData({ ...formData, repair_notes: e.target.value })}
                className="input-swiss w-full px-3 py-2"
                rows={2}
              />
            </div>

            <div>
              <Label className="overline">Fotos (optional)</Label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setPhotoFiles(Array.from(e.target.files || []))}
                className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-[#E60000] file:text-white file:font-semibold file:cursor-pointer hover:file:bg-[#B30000]"
                data-testid="damage-photos-input"
              />
              {photoFiles.length > 0 && (
                <p className="text-xs text-[#8A94A6] mt-1">{photoFiles.length} Foto(s) ausgewählt</p>
              )}
              {editing?.photos?.length > 0 && (
                <div className="mt-2 grid grid-cols-4 gap-2">
                  {editing.photos.map((p, i) => (
                    <img
                      key={i}
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`}
                      alt={`Schaden ${i + 1}`}
                      className="w-full h-20 object-cover rounded-sm border border-[#E2E4E9]"
                    />
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Abbrechen</Button>
              <Button type="submit" className="btn-primary" data-testid="save-damage-btn">
                {editing ? 'Speichern' : 'Erfassen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Damages;
